import React from 'react';
import PropTypes from 'prop-types';
import { MoreHorizontal, MoreVertical } from 'lucide-react';
import styled from 'styled-components';

import { Dropdown } from '../dropdown';
import { DeleteSharedListModal } from './components/DeleteSharedListModal';
import { DuplicateSharedListModal } from './components/DuplicateSharedListModal';
import { useSharedListsActions } from './useSharedListsActions';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 240px;
  padding: 0 0 4px 0;
  background-color: ${({ theme }) => theme.colors.neutral[0]};
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;

const Divider = styled.div`
  height: 1px;
  margin: 0 16px;
  background-color: ${({ theme }) => theme.colors.border.subtle};
`;

const Item = styled.div`
  color: ${({ theme, $isDisabled, $isDelete }) => {
    if ($isDisabled) return theme.colors.text.placeholder;
    if ($isDelete) return theme.colors.text.error;
    return theme.colors.text.main;
  }};
  text-decoration: none;
  font-size: 14px;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${({ theme, $isDisabled }) => !$isDisabled && theme.colors.neutral[20]};
  }
`;

const IconWrapper = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const SharedListsActions = ({
  list,
  isManager,
  triggerStyle,
  menuIconOrientation,
  showDuplicateList,
  queryParams,
}) => {
  const {
    menuRef,
    actionMenuItems,
    deleteList,
    duplicateList,
    handleDelete,
    handleDuplicate,
    closeDeleteModal,
    closeDuplicateModal,
    isLoadingDelete,
    isLoadingDuplicate,
  } = useSharedListsActions({ list, isManager, showDuplicateList, queryParams });

  const MoreIcon = menuIconOrientation === 'vertical' ? MoreVertical : MoreHorizontal;

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Dropdown
        ref={menuRef}
        placement="left-start"
        triggerProps={{
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
          },
        }}
        trigger={
          <div
            role="button"
            tabIndex={0}
            aria-label="More actions"
            aria-haspopup="menu"
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              ...triggerStyle,
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          >
            <MoreIcon size="16px" style={{ strokeWidth: '1.5' }} aria-hidden="true" />
          </div>
        }
      >
        <Card width="240px" height="auto" p="8px 16px" bg="neutral.0">
          {actionMenuItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <Item
                $isDisabled={item.isDisabled}
                $isDelete={item.id === 'delete'}
                role="menuitem"
                tabIndex={item.isDisabled ? -1 : 0}
                aria-disabled={item.isDisabled}
                onClick={(e) => {
                  e.stopPropagation();

                  if (item.onClick && !item.isDisabled) {
                    menuRef.current?.closeDropdown();
                    item.onClick();
                  }
                }}
                onKeyDown={(e) => {
                  if (item.isDisabled) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    menuRef.current?.closeDropdown();
                    item.onClick?.();
                  }
                }}
              >
                <IconWrapper as={item.icon} />
                <span style={{ fontSize: '14px', lineHeight: '1.4' }}>{item.title}</span>
              </Item>
              {index < actionMenuItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Card>
      </Dropdown>
      {deleteList.showModal && (
        <DeleteSharedListModal
          list={deleteList.list}
          isLoading={isLoadingDelete}
          onCloseModal={closeDeleteModal}
          onDeleteSharedList={handleDelete}
        />
      )}

      {duplicateList.showModal && (
        <DuplicateSharedListModal
          list={duplicateList.list}
          isLoading={isLoadingDuplicate}
          onCloseModal={closeDuplicateModal}
          onDuplicateSharedList={handleDuplicate}
        />
      )}
    </div>
  );
};

SharedListsActions.propTypes = {
  list: PropTypes.object.isRequired,
  isManager: PropTypes.bool.isRequired,
  triggerStyle: PropTypes.object,
  menuIconOrientation: PropTypes.oneOf(['horizontal', 'vertical']),
  showDuplicateList: PropTypes.bool,
  queryParams: PropTypes.object,
};

SharedListsActions.defaultProps = {
  triggerStyle: {},
  menuIconOrientation: 'horizontal',
  showDuplicateList: true,
  queryParams: undefined,
};
